import { WritableAtom } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { SetStateAction, useCallback, useMemo } from 'react';
import { useToastMessageState } from '../../../../../../components/ToastMessage/ToastMessageProvider';
import { IKPIRequestDataModel } from '../../../../../../data-models/kpi-requests.data-model';
import {
  managedServiceFinishReview,
  managedServiceStartReview,
} from '../../../../../../services/queries/KPIRequestsQueries';
import { getErrorMessage } from '../../../../../../services/queryHelpers';
import { kpisRequestByIdStateJ } from '../../../../../../services/state/KPI/KPIRequestsStateJ';
import { getForesightStore } from '../../../../../../util/jotai-store';

import { KPIReviewState } from './useKPIRequestReviewActions';

export type KPIReviewStateAtom = WritableAtom<KPIReviewState, [SetStateAction<KPIReviewState>], unknown>;

export function useManagedServiceActions(stateAtom: KPIReviewStateAtom, store = getForesightStore()) {
  const { get, set } = store;
  const { pushErrorToast } = useToastMessageState();
  return useMemo(() => {
    const startReview = async () => {
      const { currentRequest } = get(stateAtom);
      set(stateAtom, (prev) => ({ ...prev, isSubmitting: true }));

      let updatedRequest: IKPIRequestDataModel;
      try {
        updatedRequest = await managedServiceStartReview(currentRequest.id);
        set(stateAtom, (prev) => {
          return {
            ...prev,
            isSubmitting: false,
            currentRequest: { ...updatedRequest },
          };
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, 'Failed to start review');
        pushErrorToast({ message: errorMessage });
        set(stateAtom, (prev) => ({
          ...prev,
          isSubmitting: false,
          submissionError: errorMessage,
        }));
      }
    };

    const finishReview = async () => {
      const { currentRequest } = get(stateAtom);
      set(stateAtom, (prev) => ({ ...prev, isSubmitting: true }));

      let updatedRequest: IKPIRequestDataModel;
      try {
        updatedRequest = await managedServiceFinishReview(currentRequest.id);
        set(stateAtom, (prev) => {
          return {
            ...prev,
            isSubmitting: false,
            currentRequest: { ...updatedRequest },
          };
        });
      } catch (err) {
        const errorMessage = getErrorMessage(err, 'Failed to finish review');
        pushErrorToast({ message: errorMessage });
        set(stateAtom, (prev) => ({
          ...prev,
          isSubmitting: false,
          submissionError: errorMessage,
        }));
      }
    };

    return {
      finishReview,
      startReview,
    };
  }, [get, stateAtom, set, pushErrorToast]);
}
export type ManagedServiceActions = ReturnType<typeof useManagedServiceActions>;

/**@deprecated: implemented for v1 of kpi response form */
export function useManagedServiceActionsV1() {
  const { pushErrorToast } = useToastMessageState();

  const startReview = useAtomCallback(
    useCallback(
      async (_get, set, requestId: number) => {
        try {
          const updatedRequest = await managedServiceStartReview(requestId);
          set(kpisRequestByIdStateJ(requestId), updatedRequest);
        } catch (err) {
          console.error(err);
          pushErrorToast({ message: getErrorMessage(err, 'Failed to start review') });
        }
      },
      [pushErrorToast]
    )
  );

  const finishReview = useAtomCallback(
    useCallback(
      async (_get, set, requestId: number) => {
        try {
          const updatedRequest = await managedServiceFinishReview(requestId);
          set(kpisRequestByIdStateJ(requestId), updatedRequest);
        } catch (err) {
          pushErrorToast({ message: getErrorMessage(err, 'Failed to finish review') });
        }
      },
      [pushErrorToast]
    )
  );

  return {
    finishReview,
    startReview,
  };
}
